/**
 * EMP-WS-UI-001 — shared premium employee header (Profile + Edit Employee).
 * Presentation only: photo actions reuse the existing /photo endpoints via usePhotoActions.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Mail, Briefcase, Users, MapPin, CalendarDays, IdCard } from 'lucide-react';
import { formatCalendarDate } from '../../../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const STATUS_PILL = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-slate-100 text-slate-600 border-slate-200',
  resigned: 'bg-amber-50 text-amber-700 border-amber-200',
  terminated: 'bg-rose-50 text-rose-700 border-rose-200',
};
const DOT = { active: 'bg-emerald-500', inactive: 'bg-slate-400', resigned: 'bg-amber-500', terminated: 'bg-rose-500' };

export const StatusPill = ({ status, testid = 'profile-status', className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] font-semibold capitalize ${STATUS_PILL[status] || STATUS_PILL.inactive} ${className}`} data-testid={testid}>
    <span className={`h-1.5 w-1.5 rounded-full ${DOT[status] || DOT.inactive}`} />{status || '—'}
  </span>
);

export const initialsOf = (name) => (name || '?').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();

/** Same validation + endpoints as the legacy profile page. */
export const usePhotoActions = (employeeId, onPhoto) => {
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Unsupported format. Use JPG, PNG, or WebP.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Maximum size is 5MB.'); return; }
    const form = new FormData(); form.append('file', file);
    try {
      const res = await axios.post(`${API}/api/hr/employees/${employeeId}/photo`, form, { headers: authHeaders() });
      onPhoto({ photo: res.data.photo, photo_thumb: res.data.photo_thumb }); toast.success('Photo updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload photo'); }
  };
  const remove = async () => {
    try {
      await axios.delete(`${API}/api/hr/employees/${employeeId}/photo`, { headers: authHeaders() });
      onPhoto({ photo: null, photo_thumb: null }); toast.success('Photo removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove photo'); }
  };
  return { upload, remove };
};

const Meta = ({ icon: Icon, children, testid }) => children ? (
  <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 capitalize" data-testid={testid}><Icon className="h-3.5 w-3.5 text-slate-400" />{children}</span>
) : null;

export const EmployeeHero = ({ employee, readOnly, onPhotoUpload, onPhotoRemove, children }) => (
  <section className="lv-card ews-hero an-rise relative overflow-hidden p-5 sm:p-6" data-testid="employee-hero">
    <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-5">
      <div className="relative group shrink-0 self-start" data-testid="profile-photo-container">
        {employee.photo ? (
          <img src={employee.photo} alt={employee.full_name} className="h-[76px] w-[76px] rounded-full object-cover ring-4 ring-white shadow-md" data-testid="profile-photo" />
        ) : (
          <div className="h-[76px] w-[76px] rounded-full ews-avatar text-white flex items-center justify-center text-2xl font-bold ring-4 ring-white shadow-md" data-testid="profile-initials">{initialsOf(employee.full_name)}</div>
        )}
        {!readOnly && (
          <label className="absolute inset-0 rounded-full bg-slate-900/55 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer" title={employee.photo ? 'Change photo' : 'Upload photo'} data-testid="profile-photo-upload-label">
            <Camera className="h-5 w-5 text-white" />
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhotoUpload} data-testid="profile-photo-input" />
          </label>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight text-slate-900 leading-tight" data-testid="profile-name">{employee.full_name || employee.employee_id}</h1>
          <StatusPill status={employee.status} />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700"><IdCard className="h-3.5 w-3.5 text-violet-500" /><span data-testid="profile-employee-id">{employee.employee_id}</span></span>
          <span className="text-slate-300">|</span>
          <span className="inline-flex items-center gap-1.5 text-slate-500 truncate" data-testid="hero-work-email"><Mail className="h-3.5 w-3.5 text-slate-400" />{employee.work_email}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <Meta icon={Briefcase} testid="hero-designation">{employee.designation}</Meta>
          <Meta icon={Users} testid="hero-team">{employee.team}</Meta>
          <Meta icon={MapPin} testid="hero-location">{employee.work_location}</Meta>
          <Meta icon={CalendarDays} testid="hero-joined">{employee.date_of_joining ? `Joined ${formatCalendarDate(employee.date_of_joining)}` : null}</Meta>
        </div>
        {!readOnly && employee.photo && (
          <button type="button" onClick={onPhotoRemove} className="mt-1.5 text-[12px] text-rose-500 hover:text-rose-700 transition-colors" data-testid="profile-photo-remove-btn">Remove photo</button>
        )}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2 lg:justify-end shrink-0" data-testid="hero-actions">{children}</div>}
    </div>
  </section>
);
